import React, { useMemo } from 'react'
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'
import { useTranslation } from 'react-i18next'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { ReactComponent as DropdownIcon } from 'assets/icons/dropdown.svg'
import { useField } from 'formik'
import { ErrorMessage } from 'components/Shared/ErrorMessage'
import { useValidationSchema } from 'context/validationSchema'
import { isFieldRequired } from 'components/Shared/FormikForm'
import classNames from 'classnames'

const useStyles = makeStyles({
  defaultWidth: {
    width: '310px'
  }
})

function defaultNoop () {
}

function defaultOptionLabel (option) {
  return option.name
}

function defaultOptionValue (option) {
  return option.id
}

const filter = createFilterOptions()

export function ControlledSelectField ({
  options,
  getOptionLabel = defaultOptionLabel,
  getOptionValue = defaultOptionValue,
  onChange = defaultNoop,
  onInputChange = defaultNoop,
  label,
  disableClear = false,
  className,
  fieldName,
  inputClassName,
  textFieldClassName,
  noOptionsText,
  disabled,
  required,
  helperText,
  addObjectParams
}) {

  const [, meta, helpers] = useField(fieldName)
  const { error, touched, value } = meta
  const { schema } = useValidationSchema()

  const { setValue, setTouched } = helpers

  const internalStyles = useStyles()
  const { t } = useTranslation()

  const selectedOption = options.find(opt => getOptionValue(opt) === value) || null
  const isRequired = useMemo(() => isFieldRequired(schema, fieldName), [schema, fieldName])

  return <Autocomplete
    disableClearable={disableClear}
    options={options}
    getOptionLabel={(option => {
      if (option.createNewOption) {
        return option.label
      } else {
        return getOptionLabel(option)
      }
    })}
    className={classNames(internalStyles.defaultWidth, className)}
    onChange={(_, newSelectedOption) => {
      if (addObjectParams && newSelectedOption?.createNewOption) {
        addObjectParams.openAddDialog(newSelectedOption.inputValue)
      } else {
        const newValue = newSelectedOption
          ? getOptionValue(newSelectedOption)
          : null
        setValue(newValue)
        onChange(newValue, newSelectedOption)
      }
    }}
    onInputChange={(_, enteredText) => onInputChange(enteredText)}
    value={selectedOption}
    noOptionsText={noOptionsText || t('form.select.noOptionsText')}
    popupIcon={<DropdownIcon/>}
    disabled={disabled}
    filterOptions={(options, params) => {
      const filtered = filter(options, params)
      if (addObjectParams && params.inputValue !== '') {
        filtered.push({
          createNewOption: true,
          inputValue: params.inputValue,
          label: `${t(addObjectParams.addToken)} "${params.inputValue}"`,
        })
      }
      return filtered
    }}
    renderInput={params => (
      <TextField
        {...params}
        onBlur={() => setTouched(true)}
        error={!!error && touched}
        helperText={(selectedOption && helperText) ?
          helperText : <ErrorMessage name={fieldName}/>}
        className={textFieldClassName}
        label={label}
        variant="filled"
        fullWidth
        required={required === undefined ? isRequired : required}
        InputProps={{
          ...params.InputProps,
          className: `${params.InputProps.className} ${inputClassName}`,
          disableUnderline: true
        }}
      />
    )}
  />
}
